import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Info, ArrowUp, ArrowDown } from 'lucide-react';
import ReportWidget from './components/ReportWidget';
import { fetchMostProblematic } from '../../store/reporting/actions';
import { formatDateTimeForAPI } from '../../constants/utils';
import { formatDurationSeconds } from '../../constants/durationUtils';
import { UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';

const STATUS_COLORS = {
  ONLINE: '#10b981',
  OFFLINE: '#ef4444',
  DO_NOT_TRACK: '#9ca3af',
  // Legacy fallbacks
  OFFLINE_UNPLANNED: '#ef4444',
  OFFLINE_PLANNED: '#3b82f6',
};

const DESCENDING_DEFAULT_FIELDS = ['unplannedDowntimeSeconds', 'failures'];

/**
 * Self-fetching by default. When {@code data} is provided externally the
 * widget renders that payload and skips its Redux dispatch — used by the
 * Global Dashboard to embed the same JSX with cross-org aggregated data.
 */
const MostProblematicWidget = ({
  organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber,
  data: externalData, loading: externalLoading, error: externalError,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const {
    mostProblematic: reduxData,
    mostProblematicLoading: reduxLoading,
    mostProblematicError: reduxError,
  } = useSelector(state => state.Reporting);
  const externalMode = externalData !== undefined;
  const data = externalMode ? externalData : reduxData;
  const loading = externalMode ? !!externalLoading : reduxLoading;
  const error = externalMode ? externalError : reduxError;

  // Sort state
  const [sortField, setSortField] = useState('unplannedDowntimeSeconds');
  const [sortDirection, setSortDirection] = useState('desc');

  // Fetch data when deps change (skipped in external mode).
  useEffect(() => {
    if (externalMode) return;
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchMostProblematic(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [externalMode, dispatch, organizationId, dateRange?.startDate, dateRange?.endDate, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  const handleRefresh = useCallback(() => {
    if (!organizationId || !dateRange?.startDate || !dateRange?.endDate) return;
    dispatch(fetchMostProblematic(
      organizationId,
      formatDateTimeForAPI(dateRange.startDate),
      formatDateTimeForAPI(dateRange.endDate),
      criticality,
      locationId,
      vendorId,
      assetId,
      assetTypeId,
      contractNumber,
    ));
  }, [dispatch, organizationId, dateRange, criticality, locationId, vendorId, assetId, assetTypeId, contractNumber]);

  // Client-side sorting
  const sortedAssets = useMemo(() => {
    if (!data?.assets) return [];
    const sorted = [...data.assets];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // String comparison for text fields
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Handle nulls
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? -1 : 1;
      if (bVal == null) return sortDirection === 'asc' ? 1 : -1;

      // Numeric comparison
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
    return sorted;
  }, [data?.assets, sortField, sortDirection]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(DESCENDING_DEFAULT_FIELDS.includes(field) ? 'desc' : 'asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc'
      ? <ArrowUp size={12} className="ms-1" />
      : <ArrowDown size={12} className="ms-1" />;
  };

  const isEmpty = !data || !data.assets || data.assets.length === 0;
  const hasActiveFilters = criticality || locationId || vendorId;
  // Show the Organization column only when the rows carry a subOrgId —
  // the per-org Reporting feed leaves it null; the Global feed populates.
  const showSubOrgColumn = (data?.assets || []).some((a) => a?.subOrgId != null);

  const getStatusColor = (status) => STATUS_COLORS[status] || STATUS_COLORS.DO_NOT_TRACK;

  return (
    <ReportWidget
      title={t('reporting.most_problematic.title', 'Most Problematic Assets')}
      loading={loading}
      error={error}
      isEmpty={isEmpty}
      emptyMessage={hasActiveFilters
        ? t('shared.no_assets_found_matching_the_selected_fi', 'No assets found matching the selected filters.')
        : t('reporting.most_problematic.empty', 'No assets with unplanned downtime in the selected period.')}
      onRefresh={externalMode ? undefined : handleRefresh}
    >
      {/* Table */}
      <div className="table-responsive">
        <table className="table table-hover mb-0" style={{ fontSize: '0.85rem' }}>
          <thead>
            <tr className="text-muted">
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('assetName')}
              >
                {t('shared.asset_name', 'Asset Name')}
                <SortIcon field="assetName" />
              </th>
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('criticality')}
              >
                {t('shared.criticality', 'Criticality')}
                <SortIcon field="criticality" />
              </th>
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('locationName')}
              >
                {t('shared.location', 'Location')}
                <SortIcon field="locationName" />
              </th>
              {showSubOrgColumn && (
                <th
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => handleSort('subOrgName')}
                >
                  {t('shared.global_org.organization', 'Organization')}
                  <SortIcon field="subOrgName" />
                </th>
              )}
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('unplannedDowntimeSeconds')}
              >
                {t('reporting.most_problematic.unplanned_downtime', 'Unplanned Downtime')}
                <SortIcon field="unplannedDowntimeSeconds" />
                <Info
                  size={13}
                  className="ms-1 text-muted"
                  id="downtimeTooltip"
                  style={{ cursor: 'help' }}
                />
                <UncontrolledTooltip placement="top" target="downtimeTooltip">
                  {t('reporting.most_problematic.tooltip_downtime', 'Total duration the asset was offline due to unplanned incidents within the selected date range.')}
                </UncontrolledTooltip>
              </th>
              <th
                style={{ cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleSort('failures')}
              >
                {t('shared.failures', 'Failures')}
                <SortIcon field="failures" />
                <Info
                  size={13}
                  className="ms-1 text-muted"
                  id="failuresTooltip"
                  style={{ cursor: 'help' }}
                />
                <UncontrolledTooltip placement="top" target="failuresTooltip">
                  {t('reporting.most_problematic.tooltip_failures', 'Number of unplanned offline incidents recorded for this asset within the selected date range.')}
                </UncontrolledTooltip>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedAssets.map((asset) => (
              <tr key={asset.assetId}>
                <td>
                  <div className="d-flex align-items-center gap-2">
                    <span
                      className="rounded-circle flex-shrink-0"
                      style={{
                        width: 10,
                        height: 10,
                        backgroundColor: getStatusColor(asset.currentStatus),
                      }}
                    />
                    <span
                      style={{
                        maxWidth: '200px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'inline-block',
                      }}
                      title={asset.assetName}
                    >
                      {asset.assetName}
                    </span>
                  </div>
                </td>
                <td className='text-uppercase'>{asset.criticality ? t(`shared.${asset.criticality.toLowerCase()}`, asset.criticality) : '\u2014'}</td>
                <td>{asset.locationName || '\u2014'}</td>
                {showSubOrgColumn && <td>{asset.subOrgName || '\u2014'}</td>}
                <td className="fw-semibold">{formatDurationSeconds(asset.unplannedDowntimeSeconds, { t, compact: true, showSeconds: true })}</td>
                <td className="fw-semibold">{asset.failures}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="text-muted mt-3" style={{ fontSize: '0.8rem' }}>
        {t('reporting.most_problematic.assets_with_failures', '{{count}} asset with unplanned failures', { count: data?.totalAssets || 0 })}
        {(data?.totalAssets || 0) !== 1 ? 's' : ''}
      </div>
    </ReportWidget>
  );
};

export default MostProblematicWidget;
